/**
 * Server-side error patterns that warrant locking the storage-health gate.
 *
 * Shared between the document WebSocket (ws-message-handlers.js) and the user
 * WebSocket (user-websocket.js) so both sync paths use the same definition of
 * "this error means we can no longer guarantee durable saves."
 *
 * Patterns are intentionally narrow — anchored where the corresponding server
 * error string is prefix-shaped — to avoid spurious full-screen lockouts on
 * incidental error messages.
 */

export const DURABILITY_BLOCKING_ERROR_PATTERNS = [
  /^delta too large/i,
  /^payload too large/i,
  /^baseline too large/i,
  /^full state too large/i,
  /storage quota/i,
  /^update required/i,
  /^failed to store update/i,
];

/**
 * Transient server errors — recoverable on the next sync. The rejected
 * delta is still in local Yjs + IndexedDB and will resync via state-vector
 * exchange on the next sync-step1 (reconnect or periodic). We must NOT
 * lock the storage-health gate for these (the modal is reserved for
 * "we can't persist at all" failures). Callers should also clear pending
 * delta-acks so the 30s ack-timeout doesn't fire and lock the gate later.
 */
export const TRANSIENT_SERVER_ERROR_PATTERNS = [
  /^rate limit/i,
  /^too many requests/i,
  // The server's own storage failed for a moment (a database timeout): its
  // generic answer. The change is in the local copy; a retry stores it.
  // (Specific refusals — quota, too large, the change cap — say why.)
  /^failed to store update: server error/i,
];

export function isDurabilityBlockingServerError(message) {
  if (isTransientServerError(message)) return false;
  if (isQuotaError(message)) return false;
  const text = String(message || "");
  return DURABILITY_BLOCKING_ERROR_PATTERNS.some(pattern => pattern.test(text));
}

/**
 * The server refused a write on one map because this user may only view it.
 * It concerns that map's channel only (stop sending there, refresh the
 * role); it must not lock saving for every map.
 */
export function isPermissionDeniedError(message) {
  return /^permission denied/i.test(String(message || ""));
}

export function isQuotaError(message) {
  return /quota_exceeded|storage quota exceeded/i.test(String(message || ''));
}

/**
 * The map (or vault) holds too many changes since its last compaction, all
 * its editors together: the change is refused until someone compacts. Not a
 * storage failure: the refused change is in the local copy, and a compaction
 * (a full snapshot) stores it (readiness plan, Phase 3, "safe compaction").
 */
/**
 * S-M10: the server refused a map snapshot that does not cover every stored
 * record (a change or another client's compaction this client has not
 * seen), or one sent without coverage. Nothing changed on the server: sync
 * again and retry, without counting a failure. Matched anywhere in the
 * message: servers up to 2026-10 sent it behind "Compaction failed: ".
 */
export function isCoverageRefusal(message) {
  return /Map changed during compaction|Update Orbital to compact this map/i.test(String(message || ""));
}

export function isCompactionNeededError(message) {
  return /^too many (user crdt )?deltas|compaction required/i.test(String(message || ""));
}

export function isTransientServerError(message) {
  const text = String(message || "");
  return TRANSIENT_SERVER_ERROR_PATTERNS.some(pattern => pattern.test(text));
}

/**
 * The server was busy and stored nothing (backend save_gate.rs): the change
 * named by `deltaId` (none for a snapshot) is sent again after a pause.
 * Recognised by the pause the server suggests; older servers never send one
 * (their "too many requests" stays a transient error).
 * @param {{message?: string, retryAfterMs?: number}} message the error message
 */
export function isServerBusy(message) {
  return Number.isFinite(message?.retryAfterMs) && /^too many requests/i.test(String(message?.message || ""));
}

/**
 * The pause before sending again after `inRow` busy answers in a row (one
 * level up per refusal, one down per acknowledged change): the server's
 * suggestion doubled for each level, at most 30 s, spread at random over its
 * second half (writers refused together do not come back together).
 * @param {number} inRow the level, this refusal included (1, 2, ...)
 * @param {number} [hintMs] the server's suggestion
 * @param {() => number} [random]
 */
export function busyRetryDelay(inRow, hintMs = 1000, random = Math.random) {
  const base = Math.max(250, Number.isFinite(hintMs) ? hintMs : 1000);
  const delay = Math.min(30000, base * 2 ** Math.max(0, inRow - 1));
  return Math.round(delay * (0.5 + random() * 0.5));
}
